<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, useAttrs, useSlots } from 'vue';

  export interface SwitchProps {
    /**
     * The current checked value, can be a boolean or an array of values
     */
    checked?: boolean | Array<string | number>;

    /**
     * Prevent the user from changing the switch state.
     */
    disabled?: boolean;

    /**
     * Displays text below the input; hidden when the isReadOnly prop is truthy.
     */
    hintText?: string;

    /**
     * Unique ID for the switch, required for accessibility purposes
     */
    id?: string;

    /**
     * Label text to display with switch
     */
    label?: string;

    /**
     * HTML name property
     */
    name?: string;

    /**
     * Value associated with switch when it is `checked`.  This value is
     * used to compare against `checked` property when passed as an array.
     */
    value?: string | number;
  }

  defineOptions({
    name: 'll-switch',
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<SwitchProps>(), {
    checked: false,
    disabled: false,
    hintText: '',
    id: undefined,
    label: '',
    name: '',
    value: '',
  });

  const attrs = useAttrs();
  const slots = useSlots();

  const emit = defineEmits<{
    (e: 'update:checked', value: SwitchProps['checked']): void;
  }>();

  if (typeof props.value === 'boolean') {
    logger.warn('Switch: use `checked` to toggle the state; `value` is what will be set if `checked` is true');
  }

  if (attrs.onChange) {
    throw new Error('ll-switch: use the @update:checked event instead of @change.');
  }

  const switchAttrs = computed(() => {
    const tempAttrs = { ...attrs };

    delete tempAttrs['data-test'];
    delete tempAttrs.class;

    return tempAttrs;
  });

  const internalValue = computed({
    get() {
      return props.checked;
    },
    set(value) {
      emit('update:checked', value);
    },
  });

  const switchId = computed(() => props.id || uniqueId('switch-'));
</script>

<template>
  <div class="stash-switch" :class="attrs['class']" :data-test="attrs['data-test'] || 'stash-switch'">
    <label
      :for="switchId"
      :class="{ 'pointer-events-none': props.disabled }"
      class="flex cursor-pointer"
      data-test="stash-switch|label"
      v-bind="switchAttrs"
    >
      <span class="relative inline-block h-5 w-[34px] min-w-[34px]">
        <span
          class="stash-switch__track absolute top-0.5 h-4 w-full rounded-[10px]"
          :class="{
            'bg-blue-500': props.checked && !props.disabled,
            'bg-ice-700': !props.checked && !props.disabled,
            'stash-switch__track--disabled bg-ice-500': props.disabled,
            'stash-switch__track--on': props.checked,
            'stash-switch__track--off': !props.checked,
          }"
          data-test="stash-switch|track"
        ></span>
        <span
          class="stash-switch__control absolute left-0 size-5 rounded-[10px] border border-solid border-ice-500 bg-white shadow transition-transform duration-200 ease-in-out"
          :class="[
            {
              'translate-x-[14px]': props.checked,
              'translate-x-0': !props.checked,
              'stash-switch__control--on': props.checked,
              'stash-switch__control--off': !props.checked,
              'stash-switch__control--disabled': props.disabled,
            },
          ]"
          data-test="stash-switch|control"
        ></span>
      </span>
      <input
        :id="switchId"
        v-model="internalValue"
        type="checkbox"
        :disabled="props.disabled"
        :name="props.name"
        tabindex="0"
        :value="value"
        class="sr-only"
      />
      <span
        v-if="props.label"
        :class="{
          'text-ice-900': props.checked && !props.disabled,
          'text-ice-700': !props.checked && !props.disabled,
          'stash-switch__label-text--on': props.checked,
          'stash-switch__label-text--off': !props.checked,
          'stash-switch__label-text--disabled text-ice-500': props.disabled,
        }"
        class="stash-switch__label-text relative top-px ml-1.5"
        data-test="stash-switch|label-text"
      >
        {{ props.label }}
      </span>
    </label>
    <span
      v-if="props.hintText || slots.hint"
      class="stash-switch__hint ml-[40px] mt-1.5 block whitespace-pre-line text-xs"
      :class="{ 'stash-switch__hint--disabled text-ice-500': props.disabled }"
      data-test="stash-switch|hint"
    >
      <!-- @slot for displaying hint text -->
      <slot name="hint">{{ props.hintText }}</slot>
    </span>
  </div>
</template>
